import { getMostRecentFieldValue, getNestedSubelementsByType } from '@fs/zion-sls'
import { i18n } from '@fs/zion-locale'
import { MAKE_NEW_RECORD, NO_RELATION_TYPE } from '../../shared/relationships/relationshipConstants'
import { parseRelKey } from '../../shared/relationships/relationshipKeys'

/**
 * Resolved lazily rather than held in a module constant so it follows a locale change. The card the
 * rework replaces words this differently, so the shared query takes it as a parameter.
 *
 * @returns {string} Localized "No relation or unknown" label.
 */
export const getNoRelationLabel = () => i18n.t('relationships-card.no-relation-or-unknown', 'No relation or unknown')

/**
 * Retrieves a person's given name on its own, for labels too narrow for a full name.
 * `getPersonName({ justOneName: true })` still returns a surname, so it is not a substitute.
 *
 * @param {Object} person - The PERSON object.
 * @returns {string} The first given name, or '' when none is recorded.
 */
export const getGivenName = (person) => {
  const givenNames = getNestedSubelementsByType(person, 'NAME_GN')
  return getMostRecentFieldValue(givenNames?.[0]) || ''
}

/**
 * Wrap a relationship suggestion in the shape `FormFilterSelect` stores.
 *
 * `FormFilterSelect` always normalizes its field into `{ userInput, selectedItem, fieldValue }`,
 * with no opt-out. Seeding the default in that shape keeps its mount-time write a no-op, so
 * opening edit mode does not mark the form dirty — `isDirty` gates SAVE and the unsaved-changes
 * prompt. {@link toSavableFormData} unwraps it again for the save path.
 *
 * @param {{key: string, primaryText: string}|null} suggestion - The selected option.
 * @returns {{userInput: string, selectedItem: Object, fieldValue: string}|null} The stored shape.
 */
export const toRelationshipFieldValue = (suggestion) =>
  suggestion ? { userInput: '', selectedItem: suggestion, fieldValue: suggestion.primaryText } : null

/**
 * Flatten form values back to the structure the save path reads.
 *
 * Unwraps each row's relationship and recomputes `relationshipType` / `relationshipDirection` from
 * its key. Deriving them at this one boundary means no component keeps them in sync while editing.
 *
 * @param {Object} formData - Raw form values from the Relationships card.
 * @returns {Object} The same values, with each relationship row flattened.
 */
export const toSavableFormData = (formData) => ({
  ...formData,
  relationships:
    formData?.relationships?.map((rel) => {
      const selectedItem = rel?.relationship?.selectedItem ?? null
      const { relKey, order } = parseRelKey(selectedItem?.key)
      return {
        ...rel,
        relationship: selectedItem,
        relationshipType: relKey || null,
        relationshipDirection: order || null,
      }
    }) ?? [],
})

/**
 * Build a form row for somebody the patron has just added to the group.
 *
 * The row opens on "No relation or unknown", the same default a member with nothing recorded gets:
 * the Add Existing Person sheet only says who joins the group, never how they are related, so the
 * patron picks that here before saving. Shaped to match {@link prepareRelationshipsFields}'s rows so
 * nothing downstream has to tell a staged row from a seeded one.
 *
 * The Add New Person panel can record a relationship to the viewed person before the row is staged,
 * so a recorded one opens selected instead.
 *
 * @param {Object} params
 * @param {string} params.personId - The person joining the group.
 * @param {string} [params.personName] - Their display name, which labels the row.
 * @param {string} [params.recordId] - The record they are in now, if any.
 * @param {Object} [params.recordedRelationship] - Their relationship to the viewed person already in
 *   SLS, as `getPersonsAndRelationshipsForHousehold` returns it, so the key is from the viewed
 *   person's side.
 * @returns {Object} A row for the `relationships` field array.
 */
export const buildAddedMemberRow = ({ personId, personName = '', recordId = null, recordedRelationship = null }) => {
  const recorded = recordedRelationship?.id ? recordedRelationship : null
  const relationshipKey = recorded ? recorded.relKey : NO_RELATION_TYPE
  const { relKey, order } = parseRelKey(relationshipKey)

  return {
    formId: `rel_added_${personId}`,
    slsRelationshipId: recorded?.id ?? null,
    personId,
    recordId,
    relationshipType: relKey || null,
    relationshipDirection: order || null,
    relationship: toRelationshipFieldValue({
      key: relationshipKey,
      primaryText: recorded ? recorded.label : getNoRelationLabel(),
    }),
    value: { key: personId, primaryText: personName },
    // The save emits the move-into-this-group dispatches only for a row flagged new; without it a
    // no-relation row contributes nothing and the add saves as a no-op. A recorded relationship
    // means they are in the group already.
    isNew: !recorded,
    isDeleted: false,
    secondaryPersonPrimaryStatusChange: null,
  }
}

export const prepareRelationshipsFields = (relationships, isCreatingNewPerson = false) => {
  let formIdCounter = 1

  const relDefaults =
    relationships?.map((rel) => {
      const { relKey, order } = parseRelKey(rel?.relKey)

      return {
        formId: `rel_${formIdCounter++}`,
        slsRelationshipId: rel?.id || null,
        personId: rel?.otherPersonId || null,
        recordId: rel?.person?.recordId || null,
        relationshipType: relKey || null,
        relationshipDirection: order || null,
        // Every row starts valid, so nothing is required of the patron to proceed.
        relationship: toRelationshipFieldValue(
          rel?.label && !isCreatingNewPerson
            ? { key: rel?.relKey, primaryText: rel?.label }
            : { key: NO_RELATION_TYPE, primaryText: getNoRelationLabel() }
        ),
        value: rel?.value ? { key: rel?.otherPersonId, primaryText: rel?.value } : null,
        isNew: !rel?.id && rel?.relKey !== NO_RELATION_TYPE,
        isDeleted: false,
        secondaryPersonPrimaryStatusChange: null,
      }
    }) || []

  // Also keep track of original relationships for the save function
  const relationshipsById = {}
  relationships
    ?.filter((rel) => rel.fieldType !== NO_RELATION_TYPE) // Exclude the non-storing rows
    ?.forEach((rel) => {
      if (rel?.id) {
        const { relKey, order } = parseRelKey(rel?.relKey)
        relationshipsById[rel.id] = {
          id: rel.id,
          otherPersonId: rel.otherPersonId,
          relKey,
          order,
        }
      }
    })

  return { relationships: relDefaults, relationshipsById }
}

/**
 * Build default form values for the family members edit UI following the simplified form state structure.
 *
 * Creates a form state that matches the structure defined in USER_INTERACTION.md:
 * - focusedPerson: Contains personId and recordId
 * - relationships: Array of relationship objects with stable formIds
 * - crossRecordResolution: Tracks cross-record strategy decisions
 *
 * @param {Array<Object>} relationships Raw relationships; items may contain: id, relKey, label, value, otherPersonId.
 * @param {Object} person The focused person object with id and recordId.
 * @param {Array<Object>} households Array of household suggestion objects for the household field.
 * @param {boolean} isCreatingNewPerson Whether the card is being used in the context of creating a new person (vs editing an existing one).
 * This affects how we determine the default relationship options selected in the form.
 * @returns {Object} Initial form values object matching the simplified form state structure.
 */
export function prepareRelationshipsDefaultValues(relationships, person, households = [], isCreatingNewPerson = false) {
  const { relationships: relDefaults, relationshipsById } = prepareRelationshipsFields(
    relationships,
    isCreatingNewPerson
  )

  // Find the household matching the person's recordId or default to MAKE_NEW_RECORD
  const household =
    households.find((hh) => hh.key === person?.recordId) ?? households.find((hh) => hh.key === MAKE_NEW_RECORD)

  return {
    focusedPerson: {
      personId: person?.id || null,
      recordId: person?.recordId || null,
    },
    relationships: relDefaults,
    targetRecordId: person?.recordId || MAKE_NEW_RECORD,
    targetPersonIsPrimary: person?.primary || false,
    names: [],
    // Keep this for backward compatibility with save function
    relationshipsById,
    household,
  }
}

/**
 * @typedef {Object} PendingReviewResult
 * @property {Array<string>} newMemberIds - Person IDs that need review.
 * @property {Array<string>} newMemberIdsWithExistingRelationships - Person IDs with existing relationships in their households.
 */
